<template>
  <div
    id="slide-out"
    :class="[
      'fixed top-15.5 lg:top-21 right-0 lg:left-0 lg:right-auto',
      'h-[calc(100vh-62px)] lg:h-[calc(100vh-84px)]',
      'w-67.5 lg:w-65 bg-secondary-background shadow-lg z-1001 flex flex-col',
      'transform-gpu transition-transform duration-300 ease-out',
      isActive ? 'translate-x-0' : 'translate-x-full',
      'lg:translate-x-0'
    ]"
  >
    <div class="relative h-full flex flex-col">
      <!-- USER INFO -->
      <div class="flex justify-between items-center px-3 py-2 text-xs text-[#0e0e0e] lg:hidden">
        <div class="flex items-center gap-1">
          <i class="far fa-user-circle text-base" />
          <span class="font-semibold">
            {{ displayUsername }}
          </span>
        </div>
        <div class="flex items-center gap-1">
          <i class="fas fa-briefcase text-base" />
          <span>{{ displayRole }}</span>
        </div>
      </div>

      <!-- SCROLL MENU -->
      <div class="flex-1 overflow-y-auto">
        <ul class="py-2">
          <li
            v-for="(menu, idx) in menuList"
            :key="idx"
            :class="[
              'group text-sm border-b border-slate-200 last:border-b-0 py-0.5',
              isMenuActive(menu) ? 'bg-primary/10' : 'hover:bg-primary/5'
            ]"
          >
            <!-- MAIN MENU ITEM -->
            <component
              :is="menu.route ? 'router-link' : 'a'"
              :to="menu.route"
              :class="[
                'w-full flex items-center justify-between px-3 py-2 cursor-pointer transition-all border-l-4',
                isMenuActive(menu) ? 'border-primary' : 'border-transparent'
              ]"
              @click="onMainClick(menu, idx)"
            >
              <span class="flex-1 flex items-center">
                <i :class="`text-primary sv-slim-icon ${menu.icon}`" />
                <span class="ml-2 font-bold">
                  {{ `${idx + 1}. ${translateLabel(menu.displayName)}` }}
                </span>
              </span>

              <i
                v-if="menu.child && menu.child.length"
                class="fas fa-angle-down ml-2 transition-transform duration-150"
                :class="currentChild === idx ? 'rotate-180' : ''"
              />
            </component>

            <!-- CHILD MENU -->
            <div
              v-if="menu.child && menu.child.length"
              :class="currentChild === idx ? 'block' : 'hidden'"
              class="pl-6 pr-2 py-1"
            >
              <ul>
                <li
                  v-for="(child, i) in menu.child.filter(item => typeof item.parentName === 'string')"
                  :key="i"
                  class="mb-1"
                >
                  <router-link
                    :to="child.route"
                    :class="[
                      'flex items-center text-xs py-1 pr-3 pl-7 transition-all',
                      $route.path === child.route
                        ? 'text-primary font-semibold bg-primary/5'
                        : 'text-gray-700 hover:bg-primary/5'
                    ]"
                    @click="onChildClick"
                  >
                    <span class="flex-1 font-bold">
                      {{ `${idx + 1}.${i + 1} ${translateLabel(child.displayName)}` }}
                    </span>
                  </router-link>
                </li>
              </ul>
            </div>
          </li>
        </ul>
      </div>

      <!-- BOTTOM TOOL LINKS -->
      <div class="flex flex-col py-3 px-3 text-[13px] text-[#0e0e0e] border-t border-gray-200 bg-white space-y-3">
        <!-- mobile only -->
        <div class="grid grid-cols-2 gap-2 lg:hidden">
          <div class="flex justify-start">
            <LanguageButton direction="up" />
          </div>
          <div class="flex justify-end">
            <DocumentButton />
          </div>
        </div>

        <!-- logout mobile only -->
        <button
          type="button"
          class="w-full flex items-center justify-center gap-2 py-2 rounded-md text-[13px] font-semibold text-red-600 border border-red-300 hover:bg-red-50 active:bg-red-100 transition lg:hidden"
          @click="onLogout()"
        >
          <i class="fas fa-sign-out-alt text-sm" />
          <span>{{ $t('general.signout') }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import MenuData from '@/data/Menu';
import { getModule } from 'vuex-module-decorators';

import AuthModule from '@/store/modules/Auth';
import LanguageButton from '@/components/LanguageButton.vue';
import DocumentButton from '@/components/DocDownload.vue';
import utils from '@/services/utils';
import store from '@/store';

export default defineComponent({
  // eslint-disable-next-line vue/multi-word-component-names
  name: 'Sidebar',

  components: {
    LanguageButton,
    DocumentButton
  },

  props: {
    isActive: {
      type: Boolean,
      required: true
    },
    toggleSidebar: {
      type: Function as PropType<() => void>,
      required: true
    }
  },

  data() {
    return {
      AuthInstance: getModule(AuthModule, store),
      utilsInstance: utils,
      menuList: [...MenuData.menuList],
      userOnline: 0,
      currentChild: null as number | null,
      ioConnect: null as any,
      isLoad: false,
      token: utils.getUserPersistent().token,
      isSwitchingLegacy: false
    };
  },

  computed: {
    displayUsername(): string {
      return String(this.AuthInstance?.user?.username || '').toUpperCase();
    },

    displayRole(): string {
      return String(this.AuthInstance?.user?.role || '');
    }
  },

  watch: {
    'AuthInstance.user': {
      deep: true,
      handler() {
        this.buildMenuList();
      }
    },

    '$route.path': {
      handler() {
        const idx = this.menuList.findIndex((m: any) => this.isMenuActive(m));
        this.currentChild = idx !== -1 ? idx : null;
      }
    }
  },

  created() {
    this.buildMenuList();
  },

  methods: {
    translateLabel(key: unknown) {
      if (typeof key !== 'string' || !key.trim()) {
        return '';
      }

      try {
        return this.$t(key);
      } catch (error) {
        console.warn('i18n key error:', key, error);
        return key as string;
      }
    },

    buildMenuList() {
      const role = this.AuthInstance?.user?.role || '';
      const userPermission = this.AuthInstance?.user?.userPermission || {};
      const level = Number(this.AuthInstance?.user?.level || 0);
      const isWhiteLabel = Boolean(this.AuthInstance?.user?.isWhiteLabel);

      this.menuList = [...MenuData.menuList];

      this.menuList = this.menuList.filter((item: any) => {
        const perm = userPermission?.[item.key];
        const allowedByPerm = perm !== 'off';
        const allowedByRole = item.allow && item.allow.includes(role);
        const allowedByWhiteLabel = !item.isWhiteLabel || isWhiteLabel;

        return allowedByPerm && allowedByRole && allowedByWhiteLabel;
      });

      this.menuList = this.menuList.map((item: any) => {
        if (!item.child) return item;

        const filteredChild = item.child.filter((i: any) => {
          const allowRole = !i.allow || i.allow.includes(role);
          const allowEditKey = !i.isEditPermKey || userPermission?.[i.isEditPermKey] === 'edit';
          const allowLevel = !i.level || i.level >= level;
          const allowWhiteLabel = !i.isWhiteLabel || isWhiteLabel;

          return allowRole && allowEditKey && allowLevel && allowWhiteLabel;
        });

        return {
          ...item,
          child: filteredChild
        };
      });

      const idx = this.menuList.findIndex((m: any) => this.isMenuActive(m));
      if (idx !== -1) {
        this.currentChild = idx;
      } else {
        this.currentChild = null;
      }
    },

    normalizePath(path?: string) {
      if (!path) return '';
      return path.replace(/\/+$/, '');
    },

    isMenuActive(menu: any) {
      const currentPath = this.normalizePath(this.$route.path);

      if (menu.route) {
        const menuPath = this.normalizePath(menu.route);
        if (currentPath === menuPath || currentPath.startsWith(`${menuPath}/`)) {
          return true;
        }
      }

      if (menu.child && menu.child.length) {
        return menu.child.some((child: any) => {
          if (!child.route) return false;
          const childPath = this.normalizePath(child.route);
          return currentPath === childPath || currentPath.startsWith(`${childPath}/`);
        });
      }

      return false;
    },

    handleUserOnline(data: any) {
      this.userOnline = 0;
      const user = Object.values(data);
      user.forEach((v: any) => {
        this.userOnline += (v as any).amount;
      });
      this.isLoad = false;
    },

    onMainClick(menu: any, idx: number) {
      const isDesktop = window.innerWidth >= 1024;

      if (menu.child && menu.child.length) {
        this.toggleChild(idx);
      }

      if (!menu.route) return;

      if (!isDesktop) {
        this.closeSidebar();
      }
    },

    onChildClick() {
      const isDesktop = window.innerWidth >= 1024;
      if (!isDesktop) {
        this.closeSidebar();
      }
    },

    toggleChild(idx: number) {
      this.currentChild = idx !== this.currentChild ? idx : null;
    },

    onChildMenuClick() {
      if (window.innerWidth < 1024) {
        this.closeSidebar();
      }
    },

    closeSidebar() {
      this.toggleSidebar();
    },

    async onLogout() {
      await this.AuthInstance.Logout();

      if (this.$route.path !== '/login') {
        await this.$router.push('/login');
      }
    }
  }
});
</script>
